/**
 * Organisation hooks — list / detail / create / update / domain CRUD.
 * Used by the admin /clients screens.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  organizationsApi,
  type OrganizationDetail,
} from '@beacon/api-client';
import type { Organization } from '@beacon/core';
import type {
  OrganizationCreateInput,
  OrganizationDomainAddInput,
  OrganizationUpdateInput,
} from '@beacon/core/validation';

import { beaconClient } from '@/lib/api/client.js';

export const organizationKeys = {
  all: ['organizations'] as const,
  list: () => [...organizationKeys.all, 'list'] as const,
  detail: (id: string) => [...organizationKeys.all, 'detail', id] as const,
};

export function useOrganizations() {
  return useQuery({
    queryKey: organizationKeys.list(),
    queryFn: async (): Promise<Organization[]> => {
      const res = await organizationsApi.list(beaconClient);
      return res.organizations;
    },
    staleTime: 5 * 60_000,
  });
}

export function useOrganization(id: string | undefined) {
  return useQuery({
    queryKey: id ? organizationKeys.detail(id) : organizationKeys.all,
    queryFn: () => organizationsApi.getById(beaconClient, id!),
    enabled: Boolean(id),
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrganizationCreateInput) =>
      organizationsApi.create(beaconClient, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.list() });
    },
    onError: (err) => {
      toast.error("Couldn't create organisation", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });
}

interface UpdateVars {
  id: string;
  input: OrganizationUpdateInput;
}

export function useUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateVars) =>
      organizationsApi.update(beaconClient, id, input),
    onSuccess: (server: OrganizationDetail) => {
      qc.setQueryData(organizationKeys.detail(server.id), server);
      qc.invalidateQueries({ queryKey: organizationKeys.list() });
    },
    onError: (err) => {
      toast.error("Couldn't update organisation", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });
}

interface AddDomainVars {
  organizationId: string;
  input: OrganizationDomainAddInput;
}

export function useAddOrganizationDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, input }: AddDomainVars) =>
      organizationsApi.addDomain(beaconClient, organizationId, input),
    onSuccess: (_data, { organizationId }) => {
      qc.invalidateQueries({
        queryKey: organizationKeys.detail(organizationId),
      });
    },
    onError: (err) => {
      toast.error("Couldn't add domain", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });
}

interface RemoveDomainVars {
  organizationId: string;
  domainId: string;
}

export function useRemoveOrganizationDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, domainId }: RemoveDomainVars) =>
      organizationsApi.removeDomain(beaconClient, organizationId, domainId),
    onSuccess: (_data, { organizationId }) => {
      qc.invalidateQueries({
        queryKey: organizationKeys.detail(organizationId),
      });
    },
    onError: (err) => {
      toast.error("Couldn't remove domain", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });
}
